import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ClientEvent, SyncState } from '../constants/matrixEvents';
import logger from '../utils/logger';
import matrixService from '../services/matrixService';

/**
 * Settings Context - Global application settings
 * Manages user preferences including read receipt behavior per TI requirements
 * 
 * Settings are stored in Matrix account data:
 * - Permission config: de.gematik.tim.account.permissionconfig.pro.v1 (TI-M spec compliant)
 * - Other settings: kPrivacySettingsKey (custom extension)
 */

// TI-M spec namespace for Incoming Invites (A_25044)
// IMPORTANT: Must match messenger-client's namespace exactly for cross-app compatibility
// messenger-client uses: 'de.gematik.tim.account.permissionconfig.pro.v1' (in invite_rejection_policy_repository.dart)
const PERMISSION_CONFIG_TYPE = 'de.gematik.tim.account.permissionconfig.pro.v1';

// Custom namespace for other WebApp settings (read receipts, presence, etc.)
const OTHER_SETTINGS_TYPE = 'kPrivacySettingsKey';

// Known group names from the TI-M permissionConfig JSON schema enum.
// Forward-compatible: unknown group names from peer clients are dropped with a log warning.
const KNOWN_GROUP_NAMES = Object.freeze(['isInsuredPerson']);

// Detect if running in development mode
const isDev = process.env.NODE_ENV === 'development';

/**
 * Transform internal settings format to TI-M permissionConfig schema format
 *
 * INTEROPERABILITY WITH MESSENGER-CLIENT (Flutter app):
 * =====================================================
 * Both apps store Incoming Invites settings in Matrix account data using the
 * TI-M spec namespace: de.gematik.tim.account.permissionconfig.pro.v1
 *
 * TI-M SPEC FORMAT (used by both apps):
 * {
 *   "defaultSetting": "allow all" | "block all",
 *   "serverExceptions": { "domain.com": {}, ... },  // domains as keys
 *   "userExceptions": { "@user:server.com": {}, ... },  // user IDs as keys
 *   "groupExceptions": ["isInsuredPerson", ...]  // array of group names
 * }
 *
 * CUSTOM FIELD (_inactiveList):
 * This is a WebApp-only extension for UX preservation. The TI-M spec only
 * stores ONE list (exceptions to the default). When users toggle between
 * "allow-all" and "block-all" modes, we'd lose the other list without this.
 *
 * IMPORTANT: The ACTIVE list is ALWAYS read from serverExceptions/userExceptions.
 * We only store the INACTIVE list in the custom field. This prevents desync bugs
 * where third-party clients preserve custom fields but don't update them.
 *
 * Example: User in "allow-all" mode has blocked: [spam.com, @troll:x.com]
 *          User also had allowed entries from when they were in block-all mode.
 *          Without _inactiveList, switching modes would lose the allowed list.
 *
 * messenger-client ignores this custom field (prefixed with _) safely.
 *
 * Schema conformance (permissionConfig_V1, Draft-07):
 * - Vendored copy at src/context/__tests__/fixtures/permissionConfig_V1.schema.json
 *   (see that file for the upstream commit SHA).
 * - defaultSetting, serverExceptions, userExceptions, groupExceptions match
 *   the schema exactly (enum values, patternProperties keys, empty-object
 *   values, {groupName: ...} items shape).
 * - _inactiveList is a schema-valid ADDITIONAL property: the schema's root
 *   object does not set additionalProperties:false, so Draft-07 defaults
 *   (validation §6.5.6) allow unspecified properties.
 * - Upstream groupExceptions.items writes `groupName` as a bare keyword
 *   (not under `properties`). Under Draft-07 §4.3.1 unknown keywords inside
 *   a schema are ignored, so the items constraint is effectively empty.
 *   Our {groupName: ...} objects match the evident intent AND pass the
 *   permissive as-written schema. Regression-tested by
 *   src/context/__tests__/permissionConfigSchema.conformance.test.js.
 *
 * @param {Object} settings - Internal settings object
 * @returns {Object} TI-M permissionConfig format with custom extensions
 */
export const toPermissionConfigSchema = (settings) => {
  const { firstContactDefaultPermission, firstContactBlockedList, firstContactAllowedList } = settings;

  // TI-M spec: "exceptions" are entries that override the default behavior
  // - In "allow-all" mode: exceptions = blocked entries
  // - In "block-all" mode: exceptions = allowed entries
  const exceptionList = firstContactDefaultPermission === 'allow-all'
    ? firstContactBlockedList
    : firstContactAllowedList;

  // Convert to TI-M spec format: { "entry": {} } object structure
  // messenger-client uses same format - domains go to serverExceptions, users to userExceptions.
  // Groups use [{groupName: 'isInsuredPerson'}] per canonical JSON schema.
  const serverExceptions = {};
  const userExceptions = {};
  const groupExceptions = [];

  (exceptionList || []).forEach(item => {
    if (item.type === 'user') {
      userExceptions[item.value] = {};
    } else if (item.type === 'domain') {
      serverExceptions[item.value] = {};
    } else if (item.type === 'group' && KNOWN_GROUP_NAMES.includes(item.value)) {
      groupExceptions.push({ groupName: item.value });
    }
  });

  // Determine which list is inactive (the one we need to preserve)
  // - In "allow-all" mode: allowed list is inactive (blocked list is in exceptions)
  // - In "block-all" mode: blocked list is inactive (allowed list is in exceptions)
  const inactiveList = firstContactDefaultPermission === 'allow-all'
    ? firstContactAllowedList
    : firstContactBlockedList;

  return {
    // TI-M spec fields (compatible with messenger-client)
    defaultSetting: firstContactDefaultPermission === 'allow-all' ? 'allow all' : 'block all',
    serverExceptions,
    userExceptions,
    groupExceptions,

    // WebApp-only custom field for UX preservation when toggling modes
    // Stores the INACTIVE list only (incl. any group entries) - active list is always in
    // serverExceptions/userExceptions/groupExceptions (source of truth).
    // messenger-client will ignore this (unknown fields are safely ignored)
    _inactiveList: inactiveList || [],
  };
};

/**
 * Extract other settings (non-permission config) for storage
 * @param {Object} settings - Internal settings object
 * @returns {Object} Other settings object
 */
const toOtherSettings = (settings) => {
  return {
    ShowReadReceipts: settings.sendPublicReadReceipts,
    OnlinePresenceVisibility: !settings.alwaysAppearOffline,
    SendTypingNotificationStatus: settings.sendTypingIndicators,
    autoLogoutTimer: settings.autoLogoutTimer,
    // Browser notification settings (synced across devices)
    browserNotifications: settings.browserNotifications,
    notifyNewMessages: settings.notifyNewMessages,
    notifyInvites: settings.notifyInvites,
    notifyReactions: settings.notifyReactions,
    notifyMentions: settings.notifyMentions,
    notifySound: settings.notifySound,
    chatExpiryReminderDays: settings.chatExpiryReminderDays,
    // Note: notificationPermissionPrompted is per-browser, stored in localStorage, not here
  };
};

/**
 * Transform TI-M permissionConfig schema format to internal settings format
 *
 * PARSING STRATEGY:
 * ========================================
 * The ACTIVE list is ALWAYS parsed from TI-M spec fields (serverExceptions/userExceptions).
 * This is the source of truth and prevents desync when third-party clients update exceptions
 * but preserve our custom _inactiveList field without updating it.
 *
 * The INACTIVE list is read from _inactiveList custom field (if present) for UX preservation
 * when toggling between allow-all and block-all modes.
 *
 * @param {Object} permissionConfig - TI-M permissionConfig format (from Matrix account data)
 * @returns {Object} Internal settings format for permission fields
 */
export const fromPermissionConfigSchema = (permissionConfig) => {
  // Handle null/undefined config - return defaults
  if (!permissionConfig) {
    return {
      firstContactDefaultPermission: 'allow-all',
      firstContactBlockedList: [],
      firstContactAllowedList: [],
    };
  }

  const defaultPermission = permissionConfig.defaultSetting === 'allow all' ? 'allow-all' : 'block-all';

  // ALWAYS parse the active list from TI-M spec fields (serverExceptions, userExceptions)
  // This is the source of truth and prevents desync bugs with third-party clients
  const activeList = [];
  let idCounter = 1;

  // Parse serverExceptions - these are domain entries
  // messenger-client format: { "domain.com": {}, "other.org": {} }
  Object.keys(permissionConfig.serverExceptions || {}).forEach(server => {
    activeList.push({ id: idCounter++, value: server, type: 'domain' });
  });

  // Parse userExceptions - these are Matrix user ID entries
  // messenger-client format: { "@user:server.com": {} }
  Object.keys(permissionConfig.userExceptions || {}).forEach(user => {
    activeList.push({ id: idCounter++, value: user, type: 'user' });
  });

  // Parse groupExceptions - canonical object form [{groupName: 'isInsuredPerson'}]
  // Also accept bare-string form ['isInsuredPerson'] that some reference clients may emit.
  // Unknown group names (outside KNOWN_GROUP_NAMES) are dropped with a warning so we stay
  // forward-compatible when the enum grows.
  (permissionConfig.groupExceptions || []).forEach(entry => {
    const name = typeof entry === 'string' ? entry : entry?.groupName;
    if (!name) return;
    if (!KNOWN_GROUP_NAMES.includes(name)) {
      logger.info(`[fromPermissionConfigSchema] Dropping unknown group name: ${name}`);
      return;
    }
    activeList.push({ id: idCounter++, value: name, type: 'group' });
  });

  // Get inactive list from custom field (if present)
  const inactiveList = permissionConfig._inactiveList || [];

  // Assign active and inactive lists to the correct fields:
  // - "allow-all" mode: active = blocked, inactive = allowed
  // - "block-all" mode: active = allowed, inactive = blocked
  return {
    firstContactDefaultPermission: defaultPermission,
    firstContactBlockedList: defaultPermission === 'allow-all' ? activeList : inactiveList,
    firstContactAllowedList: defaultPermission === 'block-all' ? activeList : inactiveList,
  };
};

/**
 * Transform "other settings" (non-permission config) from Matrix account data to internal format
 * @param {Object} otherSettings - Object from Matrix account data (OTHER_SETTINGS_TYPE)
 * @returns {Object} Internal settings format for other settings
 */
const fromOtherSettings = (otherSettings) => {
  if (!otherSettings) {
    return {
      sendPublicReadReceipts: false,
      alwaysAppearOffline: true,
      sendTypingIndicators: false,
      autoLogoutTimer: 60,
      // Browser notification defaults - enabled by default
      browserNotifications: true,
      notifyNewMessages: true,
      notifyInvites: true,
      notifyReactions: true,
      notifyMentions: true,
      notifySound: true,
      // Note: notificationPermissionPrompted is per-browser, stored in localStorage
    };
  }
  return {
    sendPublicReadReceipts: !!otherSettings.ShowReadReceipts,
    alwaysAppearOffline: !otherSettings.OnlinePresenceVisibility,
    sendTypingIndicators: !!otherSettings.SendTypingNotificationStatus,
    autoLogoutTimer: typeof otherSettings.autoLogoutTimer === 'number' ? otherSettings.autoLogoutTimer : 60,
    // Browser notification settings - enabled by default (synced across devices)
    browserNotifications: typeof otherSettings.browserNotifications === 'boolean' ? otherSettings.browserNotifications : true,
    notifyNewMessages: typeof otherSettings.notifyNewMessages === 'boolean' ? otherSettings.notifyNewMessages : true,
    notifyInvites: typeof otherSettings.notifyInvites === 'boolean' ? otherSettings.notifyInvites : true,
    notifyReactions: typeof otherSettings.notifyReactions === 'boolean' ? otherSettings.notifyReactions : true,
    notifyMentions: typeof otherSettings.notifyMentions === 'boolean' ? otherSettings.notifyMentions : true,
    notifySound: typeof otherSettings.notifySound === 'boolean' ? otherSettings.notifySound : true,
    chatExpiryReminderDays: typeof otherSettings.chatExpiryReminderDays === 'number' ? otherSettings.chatExpiryReminderDays : 7,
    // Note: notificationPermissionPrompted is per-browser, stored in localStorage
  };
};

// Default settings per TI requirements:
// 1. TI-M clients must disable the sending of public read receipts by default
// 2. TI-M clients must send private read receipts if sending public read receipts is disabled
const DEFAULT_SETTINGS = {
  // Read receipt settings
  sendPublicReadReceipts: false, // TI requirement: disabled by default
  sendPrivateReadReceipts: true, // TI requirement: enabled when public is disabled
  alwaysAppearOffline: true, // TI requirement: appear offline by default
  autoLogoutTimer: isDev ? 0 : 60, // 60 minutes in production, disabled in dev

  // Typing indicator settings
  sendTypingIndicators: false, // TI requirement: disabled by default

  // Incoming Invites settings
  firstContactDefaultPermission: 'allow-all', // 'allow-all' or 'block-all'
  firstContactBlockedList: [],
  firstContactAllowedList: [],

  // Browser notification settings - enabled by default for better UX (synced across devices)
  browserNotifications: true, // Master toggle - enabled by default
  notifyNewMessages: true, // Notify for new messages (when master toggle enabled)
  notifyInvites: true, // Notify for room invites (when master toggle enabled)
  notifyReactions: true, // Notify for reactions (when master toggle enabled)
  notifyMentions: true, // Notify for mentions (future - when master toggle enabled)
  notifySound: true, // Play sound with notifications (when master toggle enabled)
  chatExpiryReminderDays: 7, // Days before server deletion to warn (0 = disabled)
  // Note: notificationPermissionPrompted is per-browser, stored in localStorage (not synced)
};

export const SettingsContext = createContext(null);

/**
 * Provider component for application settings
 */
export const SettingsProvider = ({ children }) => {
  // Start with defaults, will be overridden by Matrix account data when available
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  
  // Readiness — gates the save effect until we've observed server state
  // (via AccountData or sync PREPARED) or the fallback timer fires. Using
  // useState (not a ref) is deliberate so that flipping the flag re-runs
  // the [settings, isReady] save effect — without that, an edit made
  // before ready would sit locally and never be PUT until the next edit.
  const [isReady, setIsReady] = useState(false);

  // Short-lived ring guarding against /sync races around our own PUT. The
  // Matrix homeserver can deliver AccountData events out of order relative
  // to a PUT: a /sync already in flight with an older `since` token returns
  // an earlier server state which — if applied — regresses `settings` and
  // re-fires the save effect, corrupting the server-side record.
  //
  // Populated ONLY in the save path, and only with two content JSONs per
  // save: the pre-save baseline (the lastSeen content the PUT overlays
  // onto) and the new content being written. Recording on ingest (an
  // earlier iteration did) is wrong: it suppresses legitimate cross-session
  // updates that happen to return the account-data to any value we loaded
  // previously.
  //
  // Two distinct TTLs. The pre-save baseline protects only against stale
  // /sync replays that race our PUT — in Synapse those replays return in
  // ms because /sync long-poll wakes on our PUT's commit — so
  // PRE_SAVE_GUARD_TTL_MS is kept tight. The new-save entry guards
  // against the self-echo of our PUT, which can be delayed by /sync
  // long-polling and deserves a wider window.
  //
  // Why differentiate: the pre-save baseline doubles as "state X we loaded
  // from the server", and another session can later flip account data back
  // to exactly X (a legitimate cross-session revert). Every ms the pre-save
  // entry lives is a ms we'd drop that legitimate update. Cross-session
  // reverts in practice take at least a network round-trip plus UI time
  // (hundreds of ms on the floor), so a ~250ms pre-save TTL is below the
  // legitimate-traffic floor while still comfortably above the stale-sync
  // race window.
  const recentSaveGuardPermissionJsonRef = useRef([]);  // Array<{ json: string, expiresAt: number }>
  const recentSaveGuardOtherJsonRef = useRef([]);
  const RECENT_SAVE_GUARD_TTL_MS = 3000;      // new-save self-echo window
  const PRE_SAVE_GUARD_TTL_MS = 250;          // stale /sync race window
  const RECENT_SAVE_GUARD_MAX = 8;

  // Full content (object) last observed from the server per account-data type,
  // used to preserve unknown fields on write. Another client (or a future
  // spec version) may add fields we don't handle; a naive `toSchema(settings)`
  // PUT would wipe them. We overlay our derived fields on top of the last
  // observed content to keep unknown fields round-tripping.
  const lastSeenPermissionContentRef = useRef(null);
  const lastSeenOtherContentRef = useRef(null);

  // Tracks which client object the AccountData/Sync listeners are currently
  // bound to. Distinct from `matrixService.getClient()` so the poller (below)
  // can detect identity changes after logout/relogin. matrix-js-sdk's
  // `stopClient()` does NOT call `removeAllListeners()`, so any listener
  // bound to the previous client object remains attached to that orphaned
  // object until we explicitly `.off()` it — that's a closure leak we
  // prevent here by detaching on every transition.
  const boundClientRef = useRef(null);
  // Synchronous guard for the save effect during the rebind window. A ref
  // (not state) so the save effect's early-return is robust against React's
  // batching of `setIsReady(false)` + `setSettings(DEFAULT_SETTINGS)`: even
  // if isReady's state flip races, the ref read sees the truth this tick.
  const isRebindingRef = useRef(false);
  // Replaces the previous closure-scoped `hasMarkedReady` flag — must be a
  // ref so it survives across rebinds without resetting (we deliberately
  // reset it inside `bindTo`, not on each effect re-run).
  const hasMarkedReadyRef = useRef(false);

  // Load settings from Matrix account data when available via sync. The
  // effect runs once, but the inner poller rebinds the AccountData/Sync
  // listeners every time `matrixService.getClient()` returns a new client
  // identity — covering app remount on logout/relogin AND in-place client
  // replacement (e.g. matrixService.cleanup() + init() without a page
  // reload). Without this, the listeners stayed bound to the destroyed
  // pre-logout client and the post-relogin /sync ingest never reached the
  // store, leading to a DEFAULT-PUT that clobbered the user's
  // permission_config (NED-3621).
  useEffect(() => {
    let pollInterval = null;
    let readyFallbackTimer = null;
    let syncListener = null;

    const isPreparedState = (s) => (
      s === (SyncState && SyncState.Prepared) ||
      s === (SyncState && SyncState.Syncing) ||
      s === 'PREPARED' ||
      s === 'SYNCING'
    );

    // Flip readiness — gates the save effect. Idempotent across rebinds via
    // hasMarkedReadyRef which is reset in bindTo. Clears the fallback timer,
    // the rebind block, AND the Sync listener.
    //
    // Why detach the Sync listener here: matrix-js-sdk's `SyncApi` re-emits
    // `SyncState.Syncing → Syncing` on every successful /sync poll (the
    // "keep emitting SYNCING -> SYNCING for clients who want to do bulk
    // updates" branch in `SyncApi.doSyncLoop`, dispatched via
    // `SyncApi.updateSyncState` which calls
    // `client.emit(ClientEvent.Sync, …)` unconditionally). Our
    // `isPreparedState` matches both Prepared AND Syncing, so a
    // still-attached listener would call `seedFromCacheIfAny` on every
    // poll for the entire client lifetime. Each call merges the cached
    // schema fields back into local `settings` via
    // `setSettings(prev => ({ ...prev, ...derived }))` — which would
    // revert a user's in-flight local edit during the brief window
    // between `setSettings(newValue)` and the corresponding PUT's /sync
    // echo updating the cache. The listener is a one-shot
    // initial-sync-complete signal; once we've marked ready, its job is
    // done.
    const markReady = () => {
      if (hasMarkedReadyRef.current) return;
      hasMarkedReadyRef.current = true;
      isRebindingRef.current = false;
      // UC12X investigation diagnostic — gated to dev-mode only via
      // logger.debug (consistent with sibling logging in this file).
      // Uses functional setSettings to capture the latest state at fire
      // time without needing a dedicated ref. SettingsContext has no
      // settingsRef (unlike useInviteFiltering); reading from prev is
      // the closest equivalent.
      setSettings(prev => {
        logger.debug(`[Settings-TRACE] isReady -> TRUE; firstContactDefaultPermission=${prev && prev.firstContactDefaultPermission} allow=${(prev && prev.firstContactAllowedList || []).length} block=${(prev && prev.firstContactBlockedList || []).length}`);
        return prev;
      });
      setIsReady(true);
      if (readyFallbackTimer) {
        clearTimeout(readyFallbackTimer);
        readyFallbackTimer = null;
      }
      if (syncListener && boundClientRef.current) {
        try { boundClientRef.current.off(ClientEvent.Sync, syncListener); }
        catch (e) { logger.debug(`[SettingsContext] off(Sync) in markReady failed: ${e?.message}`); }
        syncListener = null;
      }
    };

    // Read whatever the new client has cached for our two account-data
    // types (matrix-js-sdk caches /sync-delivered events; listeners bound
    // after the cache write may NOT receive a replay, which is why the
    // listener-only approach was insufficient on relogin). Returns true
    // if at least one type was found in the cache so the caller can mark
    // ready immediately and skip the DEFAULT-PUT race.
    const seedFromCacheIfAny = (client) => {
      let seededAny = false;
      try {
        // getAccountData(type) returns MatrixEvent | undefined. The content
        // is on .getContent(). Defensive against test doubles that may
        // return raw content directly.
        const ev = client.getAccountData?.(PERMISSION_CONFIG_TYPE);
        const content = ev && typeof ev.getContent === 'function' ? ev.getContent() : ev;
        if (content && typeof content === 'object' && !Array.isArray(content)) {
          lastSeenPermissionContentRef.current = content;
          setSettings(prev => ({ ...prev, ...fromPermissionConfigSchema(content) }));
          seededAny = true;
          logger.debug('[SettingsContext] Seeded permission_config from new client cache');
        }
      } catch (e) {
        logger.debug(`[SettingsContext] Cache-seed permission lookup failed: ${e?.message}`);
      }
      try {
        const ev = client.getAccountData?.(OTHER_SETTINGS_TYPE);
        const content = ev && typeof ev.getContent === 'function' ? ev.getContent() : ev;
        if (content && typeof content === 'object' && !Array.isArray(content)) {
          lastSeenOtherContentRef.current = content;
          setSettings(prev => ({ ...prev, ...fromOtherSettings(content) }));
          seededAny = true;
          logger.debug('[SettingsContext] Seeded other_settings from new client cache');
        }
      } catch (e) {
        logger.debug(`[SettingsContext] Cache-seed other lookup failed: ${e?.message}`);
      }
      return seededAny;
    };

    // Listen for both permission config and other settings updates. Defined
    // at the effect scope so a single function identity is reused across
    // bind/detach pairs (lets `client.off(ClientEvent.AccountData, fn)`
    // actually remove the registration).
    const handleAccountData = (event) => {
      const eventType = event.getType();
      const content = event.getContent();

      // Defensive: Matrix SDK returns whatever the server sent — reject
      // non-object payloads (null, array, primitive) so downstream spreads
      // and Object.keys cannot produce corrupt account-data PUTs.
      if (eventType === PERMISSION_CONFIG_TYPE || eventType === OTHER_SETTINGS_TYPE) {
        if (!content || typeof content !== 'object' || Array.isArray(content)) {
          logger.warn(`[SettingsContext] Ignoring ${eventType} event with non-object content`);
          return;
        }
        // First observation of server state for one of our types unblocks saves.
        markReady();
      }

      if (eventType === PERMISSION_CONFIG_TYPE) {
        const contentJson = JSON.stringify(content);

        // Drop if this content matches a recent save-guard entry — either a
        // self-echo of our PUT or a /sync replay of the pre-save server
        // state our PUT overlaid. Anything else is a legitimate external
        // change and must be applied, even if its JSON happens to match a
        // value we once loaded.
        //
        // One-shot: after a match, drop the matching entry. Our own PUTs
        // are echoed once in the common case; leaving the entry behind
        // would let a later legitimate cross-session update of the same
        // content be silently suppressed. Stale /sync replays are also
        // one-time events; the TTL remains as a safety net only. If the
        // same content arrives twice (re-subscription / full-state sync
        // concurrent with an incremental), the second pass re-applies an
        // equivalent value — the save-effect idempotency gate prevents a
        // redundant PUT, so this is safe but depends on that gate.
        const now = Date.now();
        const liveGuard = recentSaveGuardPermissionJsonRef.current.filter(e => e.expiresAt > now);
        if (liveGuard.some(entry => entry.json === contentJson)) {
          recentSaveGuardPermissionJsonRef.current = liveGuard.filter(entry => entry.json !== contentJson);
          logger.debug('[SettingsContext] Dropping PERMISSION_CONFIG echo (matches recent save-guard entry)');
          return;
        }
        recentSaveGuardPermissionJsonRef.current = liveGuard;

        // Remember the full content for unknown-field preservation, and
        // apply to local state. Do NOT push into the save-guard ring —
        // that is save-initiated only.
        lastSeenPermissionContentRef.current = content;

        logger.debug(`Loading permission config from AccountData event: ${contentJson}`);
        const permissionSettings = fromPermissionConfigSchema(content);
        setSettings(prev => ({ ...prev, ...permissionSettings }));
      } else if (eventType === OTHER_SETTINGS_TYPE) {
        const contentJson = JSON.stringify(content);

        const now = Date.now();
        const liveGuard = recentSaveGuardOtherJsonRef.current.filter(e => e.expiresAt > now);
        if (liveGuard.some(entry => entry.json === contentJson)) {
          recentSaveGuardOtherJsonRef.current = liveGuard.filter(entry => entry.json !== contentJson);
          logger.debug('[SettingsContext] Dropping OTHER_SETTINGS echo (matches recent save-guard entry)');
          return;
        }
        recentSaveGuardOtherJsonRef.current = liveGuard;

        lastSeenOtherContentRef.current = content;

        const otherSettings = fromOtherSettings(content);
        logger.debug(`Loading other settings from AccountData event: ${contentJson}`);
        setSettings(prev => ({ ...prev, ...otherSettings }));
      }
    };

    const detachFrom = (client) => {
      if (!client) return;
      try { client.off(ClientEvent.AccountData, handleAccountData); }
      catch (e) { logger.debug(`[SettingsContext] off(AccountData) in detachFrom failed: ${e?.message}`); }
      if (syncListener) {
        try { client.off(ClientEvent.Sync, syncListener); }
        catch (e) { logger.debug(`[SettingsContext] off(Sync) in detachFrom failed: ${e?.message}`); }
        syncListener = null;
      }
      if (readyFallbackTimer) {
        clearTimeout(readyFallbackTimer);
        readyFallbackTimer = null;
      }
    };

    const bindTo = (client) => {
      // 1. Reset session-scoped state FIRST so the listener path and the
      //    cache-seed path see a clean baseline. Without this, user A's
      //    `lastSeen*` would bleed into user B's first PUT via the
      //    unknown-field overlay in toPermissionConfigSchema, and the
      //    save-guard rings would carry stale entries that drop a
      //    legitimate post-relogin AccountData event as an echo.
      lastSeenPermissionContentRef.current = null;
      lastSeenOtherContentRef.current = null;
      recentSaveGuardPermissionJsonRef.current = [];
      recentSaveGuardOtherJsonRef.current = [];
      hasMarkedReadyRef.current = false;
      // 2. Block the save effect for the rebind window. Cleared by markReady
      //    (which also flips isReady true). Synchronous ref so the save
      //    effect's early-return is robust against React's batching of
      //    setIsReady + setSettings landing in unspecified order.
      isRebindingRef.current = true;
      // 3. Reset state — both isReady (gates save effect) and settings
      //    (prevents previous-session in-memory leakage onto a different
      //    account; also prevents the presence side-effect at the
      //    settings.alwaysAppearOffline useEffect from writing the old
      //    user's preference to the new client).
      // UC12X investigation diagnostic — dev-mode only via logger.debug.
      // If this fires AFTER a previous markReady, the WebApp goes through
      // a false→true→false→true cycle which can cause useInviteFiltering's
      // ready-pump to re-evaluate invites with intermediate settings.
      logger.debug(`[Settings-TRACE] isReady -> FALSE (rebinding); reason=client-rebind`);
      setIsReady(false);
      setSettings(DEFAULT_SETTINGS);

      // 4. Defensive .off before .on guards StrictMode dev double-invoke
      //    and any future re-entry path that bypasses detachFrom.
      try { client.off(ClientEvent.AccountData, handleAccountData); }
      catch (e) { logger.debug(`[SettingsContext] defensive off(AccountData) in bindTo failed: ${e?.message}`); }
      client.on(ClientEvent.AccountData, handleAccountData);
      logger.debug('SettingsContext: Matrix client ready, listening for account data');

      // 5. Try a synchronous seed in case /sync already populated the
      //    new client's cache before bindTo got to run (typical when the
      //    poller catches up after the client has been syncing for a few
      //    ticks). The seed populates lastSeen, so a same-tick save effect
      //    would skip the PUT via the idempotency gate — no DEFAULT-PUT.
      const seededImmediately = seedFromCacheIfAny(client);
      if (seededImmediately) {
        markReady();
      }

      // 6. Hook the Sync state transition. PREPARED is the canonical signal
      //    that initial /sync has finished and account_data is cached.
      //    The Sync-listener path also re-seeds at PREPARED to cover the
      //    common NED-3621 case where /sync completes between bindTo and
      //    PREPARED. The PREPARED-already branch does NOT re-seed because
      //    Step 5 just ran on the same client in the same synchronous tick;
      //    `client.getAccountData` is an in-memory store read with no async
      //    boundary between the two calls.
      if (typeof client.getSyncState !== 'function') {
        // Test doubles without sync state — nothing authoritative to wait
        // for. markReady so the UI isn't wedged.
        markReady();
      } else if (!hasMarkedReadyRef.current && isPreparedState(client.getSyncState())) {
        // PREPARED already and Step 5's seed returned false (empty cache).
        // No async boundary to wait across — just mark ready.
        markReady();
      } else if (!hasMarkedReadyRef.current && client.on) {
        syncListener = (state) => {
          if (isPreparedState(state)) {
            seedFromCacheIfAny(client);
            markReady();
          }
        };
        client.on(ClientEvent.Sync, syncListener);
      }

      // 7. Last-resort fallback. Bumped from 5 s to 30 s to handle slow
      //    initial /sync without firing a DEFAULT-PUT clobber: with the
      //    previous 5 s window, a /sync that returned at ~6 s (observed in
      //    the NED-3621 reproduction at matrix-synapse.netsferedev.com)
      //    triggered the fallback BEFORE the user's permission_config
      //    arrived, then the save effect PUT empty `groupExceptions: []`
      //    over the persisted rule. The rebind block + cache seed already
      //    handle the healthy path; this timer is the safety net only and
      //    in a real session never fires. Even at fallback we attempt
      //    one more cache seed before unblocking saves so a slow-but-
      //    eventually-cached event still populates lastSeen first.
      if (!hasMarkedReadyRef.current) {
        readyFallbackTimer = setTimeout(() => {
          logger.warn('[SettingsContext] Ready-fallback timer elapsed without Sync/AccountData signal; unblocking saves');
          if (boundClientRef.current) {
            seedFromCacheIfAny(boundClientRef.current);
          }
          markReady();
        }, 30000);
      }

      boundClientRef.current = client;
    };

    // Single permanent low-rate poller. Generalizes the previous one-shot
    // retry interval at the same 500 ms cadence to also handle:
    //   client → null     (logout)
    //   null   → newClient (relogin)
    //   oldClient → newClient (cleanup+init without a null transition)
    // Cost: one ref compare per 500 ms. Cheaper than a `client-replaced`
    // event on matrixService and confines the lifecycle coupling to one
    // file.
    const tick = () => {
      const current = matrixService.getClient();
      if (current === boundClientRef.current) return;
      // Detach old → null the ref → bind new. The order matters: we never
      // want both old and new clients holding the same listener identity
      // simultaneously, and we never want bindTo to run while
      // boundClientRef still points at the old client (boundClientRef is
      // also the source of truth for the save-effect "client mismatch"
      // guard).
      if (boundClientRef.current) {
        detachFrom(boundClientRef.current);
        boundClientRef.current = null;
      }
      if (current) {
        bindTo(current);
      }
    };

    tick();
    pollInterval = setInterval(tick, 500);

    return () => {
      clearInterval(pollInterval);
      if (readyFallbackTimer) clearTimeout(readyFallbackTimer);
      if (boundClientRef.current) {
        detachFrom(boundClientRef.current);
        boundClientRef.current = null;
      }
    };
  }, []);

  // Persist settings to Matrix account data whenever they change.
  // Gated on isReady so we don't write DEFAULT_SETTINGS before /sync has
  // delivered the real server state. Depending on isReady also ensures
  // that an edit made before ready fires *will* be persisted once ready
  // flips (the effect re-runs with the current `settings`).
  useEffect(() => {
    if (!isReady) return;
    // Belt-and-suspenders rebind guards. isReady should already be false
    // during the rebind window, but a future refactor that splits
    // setIsReady(false) and setSettings(DEFAULT_SETTINGS) across an await
    // could land them in an unexpected order; the synchronous ref check
    // closes that gap. The boundClient mismatch guard separately ensures
    // we never PUT to a client identity we haven't finished binding to.
    if (isRebindingRef.current) return;
    if (boundClientRef.current && boundClientRef.current !== matrixService.getClient()) return;

    const saveToAccountData = async () => {
      const client = matrixService.getClient();
      if (!client) {
        logger.warn('SettingsContext: Client not ready, cannot save settings');
        return;
      }

      // Overlay our derived fields on the last-observed server content so
      // unknown fields added by other clients (or a future spec version)
      // survive our PUT. Spread preserves existing-key positions, so the
      // serialized order stays stable across save/ingest — the ring
      // comparison below stays valid.
      const derivedPermission = toPermissionConfigSchema(settings);
      const permissionConfig = {
        ...(lastSeenPermissionContentRef.current || {}),
        ...derivedPermission,
      };
      const permissionConfigJson = JSON.stringify(permissionConfig);

      const derivedOther = toOtherSettings(settings);
      const otherSettings = {
        ...(lastSeenOtherContentRef.current || {}),
        ...derivedOther,
      };
      const otherSettingsJson = JSON.stringify(otherSettings);

      // Per-type idempotency: skip the PUT when the *schema view* of local
      // state already equals what lastSeen would produce after a round-trip
      // through our from/to transforms. This is critical on reload: ingest
      // fires setSettings with content derived from server state, which
      // re-runs this effect. Comparing derived (pure schema fields) to a
      // derived-of-lastSeen ignores transport artefacts like the server
      // omitting fields our derivation adds by default (e.g. `_inactiveList:
      // []`), so we don't PUT just to echo the server back to itself.
      // Without this skip, the redundant PUT can race a near-simultaneous
      // user-initiated PUT (different body) and overwrite it on the server,
      // depending on ordering at the homeserver.
      const derivedFromLastSeenPermission = lastSeenPermissionContentRef.current
        ? toPermissionConfigSchema(fromPermissionConfigSchema(lastSeenPermissionContentRef.current))
        : null;
      const derivedFromLastSeenOther = lastSeenOtherContentRef.current
        ? toOtherSettings(fromOtherSettings(lastSeenOtherContentRef.current))
        : null;
      const prevPermissionJson = lastSeenPermissionContentRef.current
        ? JSON.stringify(lastSeenPermissionContentRef.current)
        : null;
      const prevOtherJson = lastSeenOtherContentRef.current
        ? JSON.stringify(lastSeenOtherContentRef.current)
        : null;
      const shouldSavePermission = !derivedFromLastSeenPermission ||
        JSON.stringify(derivedPermission) !== JSON.stringify(derivedFromLastSeenPermission);
      const shouldSaveOther = !derivedFromLastSeenOther ||
        JSON.stringify(derivedOther) !== JSON.stringify(derivedFromLastSeenOther);

      // Record both the pre-save baseline AND the new content we're about
      // to PUT into the save-guard ring BEFORE the PUTs so that /sync
      // replays of either (the stale pre-state or our own echo) get
      // dropped. Only record for types we're actually saving — the ring
      // is a self-echo guard, so an entry for a type we didn't PUT adds
      // nothing but widens the false-drop window.
      const now = Date.now();
      const pushRing = (ref, json, ttlMs) => {
        if (!json) return;
        const live = ref.current.filter(e => e.expiresAt > now && e.json !== json);
        ref.current = [...live, { json, expiresAt: now + ttlMs }].slice(-RECENT_SAVE_GUARD_MAX);
      };
      if (shouldSavePermission) {
        pushRing(recentSaveGuardPermissionJsonRef, prevPermissionJson, PRE_SAVE_GUARD_TTL_MS);
        pushRing(recentSaveGuardPermissionJsonRef, permissionConfigJson, RECENT_SAVE_GUARD_TTL_MS);
      }
      if (shouldSaveOther) {
        pushRing(recentSaveGuardOtherJsonRef, prevOtherJson, PRE_SAVE_GUARD_TTL_MS);
        pushRing(recentSaveGuardOtherJsonRef, otherSettingsJson, RECENT_SAVE_GUARD_TTL_MS);
      }

      // Each PUT is guarded independently so a failure on one does not
      // leave the other's lastSeen ref stale — a merge base that's out
      // of sync with the server re-introduces exactly the desync class
      // this change is meant to fix.
      //
      // We deliberately DO NOT roll back the ring entry on failure. If
      // the failure was a network timeout after the server had already
      // persisted (PUT succeeded server-side, response lost), a rollback
      // would leave the content out of the ring — and the eventual /sync
      // echo would be applied as a "genuine remote change", potentially
      // regressing a later local edit. Keeping the ring entry costs
      // nothing on a truly-failed PUT (subsequent edits update the ring
      // naturally) and prevents the regression when the server did
      // accept the content.
      if (shouldSavePermission) {
        try {
          await client.setAccountData(PERMISSION_CONFIG_TYPE, permissionConfig);
          lastSeenPermissionContentRef.current = permissionConfig;
        } catch (error) {
          logger.error('Failed to save permission config to account data:', error);
        }
      }

      if (shouldSaveOther) {
        try {
          await client.setAccountData(OTHER_SETTINGS_TYPE, otherSettings);
          lastSeenOtherContentRef.current = otherSettings;
        } catch (error) {
          logger.error('Failed to save other settings to account data:', error);
        }
      }

      logger.debug(`Settings saved to account data, permission=${shouldSavePermission}, other=${shouldSaveOther}`);
    };

    saveToAccountData();
  }, [settings, isReady]);

  useEffect(() => {
    const updatePresence = async () => {
      try {
        // Check if matrixService client is ready before making calls
        if (!matrixService.client) {
          logger.debug('Matrix client not ready, skipping presence update');
          return;
        }

        logger.debug(`Updating presence sharing: ${!settings.alwaysAppearOffline}`);

        if (settings.alwaysAppearOffline) {
          await matrixService.setUserPresence('offline');
          matrixService.setSendPresenceUpdates(!settings.alwaysAppearOffline);
        } else {
          matrixService.setSendPresenceUpdates(!settings.alwaysAppearOffline);
          await matrixService.setUserPresence('online');
        }
      } catch (error) {
        // Don't throw - presence updates are not critical to app functionality
        logger.error('Failed to update user presence:', error);
      }
    };

    updatePresence();
  }, [settings.alwaysAppearOffline]);

  /**
   * Update a specific setting
   * @param {string} key - Setting key
   * @param {any} value - New value
   */
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      // Accept functional updaters so callers can avoid stale-snapshot bugs
      // (e.g. Clear All fires from a confirm callback that may run after a
      // /sync delivered new entries).
      const resolved = typeof value === 'function' ? value(prev[key]) : value;
      const newSettings = { ...prev, [key]: resolved };

      // - When sendPublicReadReceipts = true → public receipts (m.read)
      // - When sendPublicReadReceipts = false → private receipts (m.read.private)
      switch (key) {
        case 'sendPublicReadReceipts':
          newSettings.sendPrivateReadReceipts = !resolved;
          logger.info(resolved
            ? 'Switched to public read receipts (m.read)'
            : 'Switched to private read receipts (m.read.private)');
          break;
        case 'sendPrivateReadReceipts':
          newSettings.sendPublicReadReceipts = !resolved;
          logger.info(resolved
            ? 'Switched to private read receipts (m.read.private)'
            : 'Switched to public read receipts (m.read)');
          break;
      }

      return newSettings;
    });
    window.dispatchEvent(new CustomEvent('settings-changed'));
  }, []);

  /**
   * Update multiple settings at once
   * @param {Object} updates - Object with setting keys and values
   */
  const updateSettings = useCallback((updates) => {
    setSettings(prev => ({ ...prev, ...updates }));
    window.dispatchEvent(new CustomEvent('settings-changed'));
  }, []);

  /**
   * Reset all settings to defaults
   */
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    logger.info('Settings reset to defaults');
  }, []);

  /**
   * Get the appropriate receipt type based on current settings
   * @returns {'m.read'|'m.read.private'|null} Receipt type to use, or null if receipts disabled
   */
  const getReceiptType = useCallback(() => {
    if (settings.sendPublicReadReceipts) {
      return 'm.read';
    }
    if (settings.sendPrivateReadReceipts) {
      return 'm.read.private';
    }
    return null; // No receipts enabled (shouldn't happen per TI requirements)
  }, [settings.sendPublicReadReceipts, settings.sendPrivateReadReceipts]);

  const value = {
    settings,
    isReady,
    updateSetting,
    updateSettings,
    resetSettings,
    getReceiptType,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access settings context
 * @returns {Object} Settings context value
 */
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

/**
 * Hook to get just the receipt type for convenience
 * @returns {'m.read'|'m.read.private'|null} Current receipt type
 */
export const useReceiptType = () => {
  const { getReceiptType } = useSettings();
  return getReceiptType();
};
